import React, { useEffect, useRef, useState } from "react";
import { FaceMesh } from "@mediapipe/face_mesh";
import { Loader2, Video as VideoIcon, X, Circle, Square, Sparkles } from "lucide-react";
import { toast } from "sonner";

/**
 * PlyxLiveRecorder — opens the device camera, runs MediaPipe FaceMesh on
 * every frame, overlays per-landmark beauty effects on a canvas, then
 * records the canvas via MediaRecorder so the uploaded clip permanently
 * carries the AR effect baked in.
 *
 * AR effects available (real face-tracking, NOT just CSS):
 *  • smooth — gaussian-blur the skin region only (keeps eyes/lips sharp)
 *  • glow   — bright dodge over forehead + cheeks
 *  • sparkle — tiny stars on each eye landmark
 *  • blush   — soft red dot on cheekbone landmarks
 *
 * On stop, we pass the resulting Blob via onCapture(file).
 */
const AR_PRESETS = [
  { code: "none",    label: "None",    desc: "Just the camera, no AR" },
  { code: "smooth",  label: "Smooth",  desc: "Soft-focus skin, sharp eyes" },
  { code: "glow",    label: "Glow",    desc: "Lit-from-within highlight" },
  { code: "sparkle", label: "Sparkle", desc: "Star sparkle on the eyes" },
  { code: "blush",   label: "Blush",   desc: "Soft pink cheek bloom" },
];

// FaceMesh landmark indices we care about.
// Reference: https://github.com/google/mediapipe/blob/master/mediapipe/python/solutions/face_mesh_connections.py
const LANDMARK = {
  leftEye:  [33, 133, 159, 145, 153, 144],   // around left eye
  rightEye: [362, 263, 386, 374, 380, 373],  // around right eye
  leftCheek:  [205, 187, 192, 213],
  rightCheek: [425, 411, 416, 433],
  forehead:   [10, 9, 8, 151, 337, 299, 333],
};

const MAX_DURATION_SEC = 60; // hard-cap live recordings at 60s

const PlyxLiveRecorder = ({ onCapture, onCancel }) => {
  const videoRef = useRef(null);
  const canvasRef = useRef(null);
  const streamRef = useRef(null);
  const faceMeshRef = useRef(null);
  const recorderRef = useRef(null);
  const chunksRef = useRef([]);
  const rafRef = useRef(null);
  const latestLandmarksRef = useRef(null);
  const startedAtRef = useRef(null);
  const [preset, setPreset] = useState("smooth");
  const presetRef = useRef(preset);
  const [ready, setReady] = useState(false);
  const [recording, setRecording] = useState(false);
  const [elapsed, setElapsed] = useState(0);
  const [bootError, setBootError] = useState(null);

  useEffect(() => { presetRef.current = preset; }, [preset]);

  // Boot camera + FaceMesh once on mount.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        // 1. Open camera (portrait 9:16 hint).
        const stream = await navigator.mediaDevices.getUserMedia({
          video: { width: { ideal: 720 }, height: { ideal: 1280 }, facingMode: "user" },
          audio: true,
        });
        if (cancelled) { stream.getTracks().forEach(t => t.stop()); return; }
        streamRef.current = stream;
        const v = videoRef.current;
        if (!v) return;
        v.srcObject = stream;
        await v.play();

        // 2. Spin up FaceMesh — load model weights from the JsDelivr CDN.
        const fm = new FaceMesh({
          locateFile: (file) => `https://cdn.jsdelivr.net/npm/@mediapipe/face_mesh/${file}`,
        });
        fm.setOptions({
          maxNumFaces: 1,
          refineLandmarks: true,
          minDetectionConfidence: 0.5,
          minTrackingConfidence: 0.5,
        });
        fm.onResults((results) => {
          latestLandmarksRef.current = results.multiFaceLandmarks?.[0] || null;
        });
        faceMeshRef.current = fm;

        // 3. Start the render+detect loop.
        const c = canvasRef.current;
        if (!c) return;
        // Wait until the video has REAL dimensions — Safari sometimes
        // reports 0x0 on the first frame and the canvas would end up
        // looking like a tiny vertical strip.
        await new Promise((resolve) => {
          const trySize = () => {
            const w = v.videoWidth || 0;
            const h = v.videoHeight || 0;
            if (w > 0 && h > 0) {
              c.width = w;
              c.height = h;
              resolve();
            } else {
              setTimeout(trySize, 80);
            }
          };
          trySize();
        });
        const ctx = c.getContext("2d");

        const tick = async () => {
          if (cancelled || !videoRef.current) return;
          const vv = videoRef.current;
          if (vv.readyState >= 2) {
            try { await fm.send({ image: vv }); } catch { /* ignore */ }
            renderFrame(ctx, vv, c.width, c.height, latestLandmarksRef.current, presetRef.current);
          }
          rafRef.current = requestAnimationFrame(tick);
        };
        tick();
        if (!cancelled) setReady(true);
      } catch (err) {
        // Permission denied / no camera / etc. Log as info — it's an
        // expected condition in many headless / desktop environments.
        console.info("[PlyxLiveRecorder] camera unavailable:", err?.name || err);
        if (!cancelled) setBootError(err?.message || "Could not access your camera.");
      }
    })();

    return () => {
      cancelled = true;
      if (rafRef.current) cancelAnimationFrame(rafRef.current);
      try { faceMeshRef.current?.close(); } catch { /* ignore */ }
      streamRef.current?.getTracks().forEach(t => t.stop());
      try { recorderRef.current?.stop(); } catch { /* ignore */ }
    };
  }, []);

  // Recording timer — also auto-stops at MAX_DURATION_SEC.
  useEffect(() => {
    if (!recording) return;
    startedAtRef.current = Date.now();
    const id = setInterval(() => {
      const sec = Math.floor((Date.now() - startedAtRef.current) / 1000);
      setElapsed(sec);
      if (sec >= MAX_DURATION_SEC) stopRecording();
    }, 500);
    return () => clearInterval(id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [recording]);

  const startRecording = () => {
    if (!canvasRef.current) return;
    // Use the canvas stream (with AR baked in) + the original mic track.
    const canvasStream = canvasRef.current.captureStream(30);
    const audioTracks = streamRef.current?.getAudioTracks() || [];
    audioTracks.forEach(t => canvasStream.addTrack(t));
    chunksRef.current = [];
    // PREFER mp4/h264 over webm so Safari (iOS + macOS) can play the
    // resulting clip back without server-side transcode. Many Chromium
    // builds also support video/mp4 now. Fall back to webm/vp8 only if
    // the browser doesn't support any mp4 variant.
    const mimeCandidates = [
      "video/mp4;codecs=h264,aac",
      "video/mp4;codecs=avc1.42E01E,mp4a.40.2",
      "video/mp4",
      "video/webm;codecs=vp9,opus",
      "video/webm;codecs=vp8,opus",
      "video/webm",
    ];
    const mimeType = mimeCandidates.find(m => MediaRecorder.isTypeSupported(m)) || "";
    let rec;
    try {
      rec = new MediaRecorder(canvasStream, mimeType ? { mimeType } : undefined);
    } catch (err) {
      toast.error("Your browser doesn't support video recording.");
      return;
    }
    rec.ondataavailable = (e) => { if (e.data.size > 0) chunksRef.current.push(e.data); };
    rec.onstop = () => {
      const recMime = rec.mimeType || "video/webm";
      const blob = new Blob(chunksRef.current, { type: recMime });
      // Pick the right extension based on the actual mime type the recorder ended up using.
      const ext = recMime.includes("mp4") ? "mp4" : (recMime.includes("webm") ? "webm" : "bin");
      const file = new File([blob], `plyx-live-${Date.now()}.${ext}`, { type: recMime });
      onCapture?.(file);
    };
    rec.start(250);  // emit a chunk every 250ms
    recorderRef.current = rec;
    setElapsed(0);
    setRecording(true);
  };

  const stopRecording = () => {
    if (recorderRef.current && recorderRef.current.state !== "inactive") {
      try { recorderRef.current.stop(); } catch { /* ignore */ }
    }
    setRecording(false);
  };

  return (
    <div className="fixed inset-0 z-[80] bg-black flex flex-col" data-testid="plyx-live-recorder">
      <div className="flex items-center justify-between px-4 py-3 text-white">
        <button
          type="button"
          onClick={onCancel}
          data-testid="plyx-live-cancel"
          className="w-9 h-9 rounded-full bg-white/10 hover:bg-white/20 flex items-center justify-center"
          aria-label="Close"
        >
          <X className="w-4 h-4"/>
        </button>
        <div className="text-xs uppercase tracking-[0.25em] font-semibold flex items-center gap-2">
          <Sparkles className="w-3.5 h-3.5 text-[#FFB42D]"/> Live AR record
        </div>
        <div className="w-9"/>
      </div>

      <div className="flex-1 relative flex items-center justify-center">
        {/* Hidden source video — never displayed; canvas is what the user sees. */}
        <video ref={videoRef} playsInline muted className="hidden"/>
        <canvas
          ref={canvasRef}
          className="rounded-lg shadow-2xl"
          style={{
            maxHeight: "100%",
            maxWidth: "100%",
            height: "auto",
            width: "auto",
            objectFit: "contain",
            transform: "scaleX(-1)",  // mirror so it feels like a mirror, not a back camera
          }}
          data-testid="plyx-live-canvas"
        />
        {!ready && !bootError && (
          <div className="absolute inset-0 flex items-center justify-center text-white">
            <div className="text-center">
              <Loader2 className="w-8 h-8 animate-spin mx-auto mb-3"/>
              <div className="text-xs uppercase tracking-wider opacity-70">Booting camera + AR…</div>
            </div>
          </div>
        )}
        {bootError && (
          <div className="absolute inset-0 flex items-center justify-center text-white px-6">
            <div className="text-center max-w-sm">
              <div className="text-sm text-red-300">{bootError}</div>
              <div className="text-xs text-white/50 mt-2">Allow camera + microphone permission in your browser and try again.</div>
            </div>
          </div>
        )}
        {recording && (
          <div className="absolute top-3 left-1/2 -translate-x-1/2 px-3 py-1 rounded-full bg-red-600 text-white text-xs font-bold flex items-center gap-2 animate-pulse" data-testid="plyx-live-rec-timer">
            <span className="w-2 h-2 rounded-full bg-white"/> REC · {elapsed}s / {MAX_DURATION_SEC}s
          </div>
        )}
      </div>

      {/* Filter strip */}
      <div className="px-4 pt-2 pb-1">
        <div className="flex gap-2 overflow-x-auto no-scrollbar" data-testid="plyx-live-filter-strip">
          {AR_PRESETS.map((p) => (
            <button
              key={p.code}
              type="button"
              onClick={() => setPreset(p.code)}
              disabled={recording}
              data-testid={`plyx-live-filter-${p.code}`}
              title={p.desc}
              className={`shrink-0 px-3 py-1.5 rounded-full text-xs font-medium border transition-all
                          ${preset === p.code
                            ? "bg-white text-[#0A0A0A] border-white"
                            : "border-white/20 text-white/80 hover:border-white/40"}`}
            >
              {p.label}
            </button>
          ))}
        </div>
      </div>

      {/* Record button */}
      <div className="px-4 pb-6 pt-3 flex items-center justify-center">
        {!recording ? (
          <button
            type="button"
            onClick={startRecording}
            disabled={!ready}
            data-testid="plyx-live-start"
            className="w-16 h-16 rounded-full border-4 border-white disabled:opacity-50 flex items-center justify-center hover:scale-95 transition-transform"
            aria-label="Start recording"
          >
            <Circle className="w-12 h-12 text-red-600 fill-red-600"/>
          </button>
        ) : (
          <button
            type="button"
            onClick={stopRecording}
            data-testid="plyx-live-stop"
            className="w-16 h-16 rounded-full border-4 border-white flex items-center justify-center hover:scale-95 transition-transform"
            aria-label="Stop recording"
          >
            <Square className="w-7 h-7 text-red-600 fill-red-600"/>
          </button>
        )}
      </div>
      <style>{`
        .no-scrollbar::-webkit-scrollbar { display: none; }
        .no-scrollbar { scrollbar-width: none; }
      `}</style>
    </div>
  );
};

// ── Frame rendering ─────────────────────────────────────────────────────────
// Reads from the <video> element + landmark list, paints the canvas with the
// requested AR effect baked in.
function renderFrame(ctx, video, w, h, landmarks, preset) {
  ctx.save();
  ctx.clearRect(0, 0, w, h);
  ctx.drawImage(video, 0, 0, w, h);

  if (!landmarks || preset === "none") {
    ctx.restore();
    return;
  }

  // All landmarks have x,y in [0,1] relative to the video frame.
  if (preset === "smooth") {
    // Approximate "skin smoothing" — composite a softly-blurred copy of the
    // video on top of the cheek/forehead regions only. Eyes + lips stay
    // sharp by being painted last.
    ctx.filter = "blur(6px) brightness(1.06) saturate(1.08)";
    paintFaceMask(ctx, w, h, landmarks);
    ctx.filter = "none";
  } else if (preset === "glow") {
    // Soft radial bloom over the forehead + cheek points.
    [...LANDMARK.forehead, ...LANDMARK.leftCheek, ...LANDMARK.rightCheek].forEach((idx) => {
      const lm = landmarks[idx]; if (!lm) return;
      const x = lm.x * w, y = lm.y * h;
      const grad = ctx.createRadialGradient(x, y, 0, x, y, 80);
      grad.addColorStop(0, "rgba(255, 220, 180, 0.35)");
      grad.addColorStop(1, "rgba(255, 220, 180, 0)");
      ctx.fillStyle = grad;
      ctx.beginPath();
      ctx.arc(x, y, 80, 0, Math.PI * 2);
      ctx.fill();
    });
  } else if (preset === "sparkle") {
    // Star sparkles on each eye centre.
    const eyeIdxs = [...LANDMARK.leftEye, ...LANDMARK.rightEye];
    eyeIdxs.forEach((idx) => {
      const lm = landmarks[idx]; if (!lm) return;
      drawStar(ctx, lm.x * w, lm.y * h, 6 + Math.random() * 4, "rgba(255,255,255,0.85)");
    });
  } else if (preset === "blush") {
    // Pink radial bloom on each cheek.
    [...LANDMARK.leftCheek, ...LANDMARK.rightCheek].forEach((idx) => {
      const lm = landmarks[idx]; if (!lm) return;
      const x = lm.x * w, y = lm.y * h;
      const grad = ctx.createRadialGradient(x, y, 0, x, y, 55);
      grad.addColorStop(0, "rgba(255, 80, 120, 0.25)");
      grad.addColorStop(1, "rgba(255, 80, 120, 0)");
      ctx.fillStyle = grad;
      ctx.beginPath();
      ctx.arc(x, y, 55, 0, Math.PI * 2);
      ctx.fill();
    });
  }
  ctx.restore();
}

// Repaint blurred copy of the video clipped to a polygon roughly covering the
// cheek + forehead region (eyes + lips left untouched).
function paintFaceMask(ctx, w, h, landmarks) {
  const cheekIdxs = [10, 67, 109, 151, 337, 299, 333, 152, 175, 199, 200, 152];
  ctx.save();
  ctx.beginPath();
  cheekIdxs.forEach((idx, i) => {
    const lm = landmarks[idx]; if (!lm) return;
    const x = lm.x * w, y = lm.y * h;
    if (i === 0) ctx.moveTo(x, y); else ctx.lineTo(x, y);
  });
  ctx.closePath();
  ctx.clip();
  ctx.drawImage(document.querySelector("video"), 0, 0, w, h);
  ctx.restore();
}

function drawStar(ctx, cx, cy, r, color) {
  ctx.save();
  ctx.translate(cx, cy);
  ctx.fillStyle = color;
  ctx.beginPath();
  for (let i = 0; i < 10; i++) {
    const ang = (Math.PI * 2 * i) / 10;
    const rr = i % 2 === 0 ? r : r * 0.45;
    ctx.lineTo(Math.cos(ang) * rr, Math.sin(ang) * rr);
  }
  ctx.closePath();
  ctx.fill();
  ctx.restore();
}

export default PlyxLiveRecorder;
export { VideoIcon };
